import { Outlet, Link, useLocation } from "react-router-dom";
import { Toaster } from "../components/ui/sonner";
import { useLanguage } from "@/hooks/useLanguage";
import { motion } from "framer-motion";
import usePageTitle from "@/hooks/usePageTitle";

// Custom navbar component for auth pages with Apple-style design
const AuthNavbar = () => {
  const { t, currentLanguage } = useLanguage();

  // Animation variants for navbar elements
  const navbarVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: -5 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <motion.nav
      className="fixed top-0 left-0 right-0 z-50 bg-white/10 backdrop-blur-xl border-b border-white/10 shadow-sm w-full"
      initial="hidden"
      animate="visible"
      variants={navbarVariants}
    >
      <div className="container mx-auto px-6 py-4 flex justify-between items-center">
        <motion.div
          className={`flex items-center ${
            currentLanguage === "ar" ? "space-x-3" : "space-x-3"
          }`}
          variants={itemVariants}
        >
          <Link
            to="/"
            className="flex items-center hover:opacity-90 transition-opacity"
          >
            <img
              src="/images/logo/logo-star.png"
              alt="Logo"
              className="h-8 w-auto"
            />
            <div className="hidden md:block h-5 w-px bg-white/20 mx-3"></div>
            <div className="hidden md:block text-white font-light text-base">
              {t("appDesc1")}
            </div>
          </Link>
        </motion.div>

        <motion.div className="flex items-center" variants={itemVariants}>
          <div className="text-white/90 text-sm">
            {currentLanguage === "ar" ? (
              <Link
                to="#"
                className="text-white/90 hover:text-white transition-colors px-3 py-1.5 rounded-full hover:bg-white/10"
                onClick={() =>
                  document.getElementById("english-admin")?.click()
                }
              >
                English
              </Link>
            ) : (
              <Link
                to="#"
                className="text-white/90 hover:text-white transition-colors px-3 py-1.5 rounded-full hover:bg-white/10"
                onClick={() => document.getElementById("arabic-admin")?.click()}
              >
                العربية
              </Link>
            )}
          </div>
        </motion.div>
      </div>
    </motion.nav>
  );
};

const AuthLayout = () => {
  const { t, switchLanguage, currentLanguage } = useLanguage();
  const location = useLocation();

  // Set a default page title based on the current route
  const getAuthPageTitle = () => {
    const path = location.pathname;

    if (path.includes("/login")) return "login";
    if (path.includes("/register")) return "register";
    if (path.includes("/forgot-password")) return "forgotPassword";
    if (path.includes("/reset-password")) return "resetPassword";

    // Default title for auth pages
    return "authentication";
  };

  // Use the custom hook to set the page title
  usePageTitle(getAuthPageTitle());

  return (
    <div className="min-h-screen overflow-hidden">
      {/* Apple-style gradient background */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-black">
          {/* Subtle mesh gradient overlay */}
          <div className="absolute inset-0 opacity-40 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-blue-900/20 via-transparent to-transparent"></div>
          <div className="absolute inset-0 opacity-30 bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-gradient-stops))] from-indigo-900/20 via-transparent to-transparent"></div>

          {/* Subtle noise texture */}
          <div
            className="absolute inset-0 opacity-[0.03] mix-blend-soft-light"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' fill='%23FFFFFF'/%3E%3C/svg%3E")`,
              backgroundSize: "200px",
            }}
          ></div>

          {/* Animated subtle glow effect */}
          <div
            className="absolute top-1/4 left-1/4 w-1/2 h-1/2 bg-blue-500/10 rounded-full filter blur-[100px] animate-pulse"
            style={{ animationDuration: "7s" }}
          ></div>
          <div
            className="absolute bottom-1/3 right-1/4 w-1/3 h-1/3 bg-indigo-500/10 rounded-full filter blur-[100px] animate-pulse"
            style={{ animationDuration: "10s", animationDelay: "1s" }}
          ></div>
        </div>
      </div>

      {/* Add the AuthNavbar */}
      <AuthNavbar />

      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen p-4 pt-24">
        {/* Form container - Apple style */}
        <motion.div
          className="w-full max-w-md mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          {/* Logo at the top of auth forms */}
          <motion.div
            className="flex justify-center mb-8"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
          >
            <img
              src="/images/logo/darkThemeLogo.png"
              alt="Tabuk Health Cluster"
              className="h-36 w-auto opacity-60 "
            />
          </motion.div>

          <motion.div
            className="bg-white/10 backdrop-blur-2xl rounded-3xl shadow-2xl overflow-hidden border border-white/20"
            initial={{ scale: 0.98 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            style={{
              boxShadow:
                "0 10px 40px -10px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)",
            }}
          >
            <Outlet />
          </motion.div>

          {/* Footer with Apple-style design */}
          <motion.div
            className="mt-8 text-center text-xs text-white/70 space-y-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <div className="font-normal text-white/80">{t("appDesc2")}</div>
            <div className="pt-4 font-bold  text-white/60">{t("appDesc3")}</div>
          </motion.div>
        </motion.div>
      </div>

      {/* Toast notifications */}
      <Toaster position="top-center" />

      {/* Hidden language toggle inputs */}
      <div className="hidden">
        <input
          type="radio"
          name="language"
          id="arabic-admin"
          checked={currentLanguage === "ar"}
          onChange={() => switchLanguage("ar")}
        />
        <input
          type="radio"
          name="language"
          id="english-admin"
          checked={currentLanguage === "en"}
          onChange={() => switchLanguage("en")}
        />
      </div>
    </div>
  );
};

export default AuthLayout;
